import api from "./api";
import { LeaveType } from "../types/leave";

export interface LeaveApplication {
  id: number;
  application_number: string;
  leave_type: string;
  start_date: string;
  end_date: string;
  number_of_days: number;
  cancelled_days?: number;
  reason: string;
  status:
    | "pending"
    | "approved"
    | "rejected"
    | "cancelled"
    | "partially_cancelled"
    | "draft"
    | "cancellation_pending"
    | "returned";
  applied_date: string;
  employee_name?: string;
  employee_code?: string;
  department_name?: string;
  designation?: string;
}

export interface LeaveBalance {
  leave_type: string;
  available_days: number;
  used_days: number;
  total_entitlement: number;
  leave_type_id?: number;
}

export interface PendingApproval {
  id: number;
  application_number: string;
  employee_name: string;
  employee_code: string;
  department_name: string;
  leave_type: string;
  start_date: string;
  end_date: string;
  number_of_days: number;
  reason: string;
  status: string;
  approval_level: number;
  action_type?: string;
  cancellation_reason?: string;
  cancellation_requested_date?: string;
  substitute_name?: string;
  substitute_code?: string;
}

export const leaveService = {
  // Leave Types CRUD operations

  // Leave Applications
  applyForLeave: async (leaveData: any) => {
    // If leaveData is not FormData, it will be standard JSON post
    const response = await api.post("/leaves/apply", leaveData);
    return response.data;
  },

  reviseReturnedLeave: async (leaveId: number, leaveData: FormData) => {
    const response = await api.patch(`/leaves/${leaveId}/revise`, leaveData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },

  resubmitReturnedLeave: async (leaveId: number) => {
    const response = await api.post(`/leaves/${leaveId}/resubmit`);
    return response.data;
  },

  getMyLeaves: async (year?: number): Promise<LeaveApplication[]> => {
    // Only add year parameter if explicitly provided
    const url = year ? `/leaves/my-leaves?year=${year}` : "/leaves/my-leaves";
    const response = await api.get(url);
    return response.data;
  },

  // Leave Type Management
  getLeaveTypes: async (): Promise<LeaveType[]> => {
    const response = await api.get("/leave-types");
    return response.data;
  },

  getLeaveType: async (id: number): Promise<LeaveType> => {
    const response = await api.get(`/leave-types/${id}`);
    return response.data;
  },

  createLeaveType: async (
    leaveType: Partial<LeaveType>,
  ): Promise<{ id: number }> => {
    const response = await api.post("/leave-types", leaveType);
    return response.data;
  },

  updateLeaveType: async (
    id: number,
    leaveType: Partial<LeaveType>,
  ): Promise<void> => {
    await api.put(`/leave-types/${id}`, leaveType);
  },

  deleteLeaveType: async (id: number): Promise<void> => {
    await api.delete(`/leave-types/${id}`);
  },

  getLeaveDetails: async (leaveId: number) => {
    const response = await api.get(`/leaves/${leaveId}/details`);
    return response.data;
  },

  cancelLeave: async (leaveId: number, daysToCancel?: number) => {
    const response = await api.patch(`/leaves/${leaveId}/cancel`, {
      ...(daysToCancel !== undefined && { days_to_cancel: daysToCancel }),
    });
    return response.data;
  },

  requestCancellation: async (
    leaveId: number,
    reason: string,
    daysToCancel?: number,
  ) => {
    const response = await api.post(`/leaves/${leaveId}/request-cancellation`, {
      reason,
      ...(daysToCancel !== undefined && { days_to_cancel: daysToCancel }),
    });
    return response.data;
  },

  getLeaveBalance: async (): Promise<LeaveBalance[]> => {
    const response = await api.get("/leaves/balance");
    return response.data;
  },

  getAvailableLeaveTypes: async () => {
    const response = await api.get("/leaves/available-types");
    return response.data;
  },

  getPendingApprovals: async (): Promise<PendingApproval[]> => {
    const response = await api.get("/approvals/pending");
    return response.data;
  },

  processApproval: async (
    applicationId: number,
    action: "approved" | "rejected" | "return",
    comments?: string,
  ) => {
    const response = await api.post("/approvals/process", {
      applicationId,
      action,
      comments,
    });
    return response.data;
  },

  getActiveEmployees: async (options?: {
    includeOtherDepartments?: boolean;
    departmentId?: number;
    all?: boolean;
  }) => {
    const params = new URLSearchParams();
    if (options?.includeOtherDepartments) {
      params.append("include_other_departments", "true");
    }
    if (options?.departmentId) {
      params.append("department_id", options.departmentId.toString());
    }
    if (options?.all) {
      params.append("all", "true");
    }

    const url = params.toString()
      ? `/employees/active?${params.toString()}`
      : "/employees/active";

    const response = await api.get(url);
    // API now returns { employees, is_vcapprove, substitute_mandatory, position? }
    // For backward compat, handle both old (flat array) and new (object) shapes
    if (response.data && Array.isArray(response.data)) {
      return {
        employees: response.data,
        is_vcapprove: false,
        substitute_mandatory: false,
        current_department_id: null,
        current_department_name: null,
      };
    }
    return response.data;
  },

  getPendingSubstituteRequests: async () => {
    const response = await api.get("/substitute/pending");
    return response.data;
  },

  getMySubstitutions: async () => {
    const response = await api.get("/substitute/my-substitutions");
    return response.data;
  },

  processSubstituteAction: async (
    applicationId: number,
    action: "accept" | "reject",
    remarks?: string,
  ) => {
    const response = await api.post(`/substitute/${applicationId}/process`, {
      action,
      remarks,
    });
    return response.data;
  },

  getLeaveRequirements: async (leaveTypeId: number) => {
    const response = await api.get(`/leaves/requirements/${leaveTypeId}`);
    return response.data;
  },

  getHolidays: async (startDate: string, endDate: string) => {
    const response = await api.get("/leaves/holidays", {
      params: {
        start_date: startDate,
        end_date: endDate,
      },
    });
    return response.data;
  },
};

export const hrService = {
  getStats: async () => {
    const response = await api.get("/hr/stats");
    return response.data;
  },

  getEmployees: async () => {
    const response = await api.get("/hr/employees");
    return response.data;
  },

  getAllLeaves: async (filters?: {
    status?: string;
    department?: string;
    page?: number;
    limit?: number;
  }) => {
    const params = new URLSearchParams();
    if (filters?.status) params.append("status", filters.status);
    if (filters?.department) params.append("department", filters.department);
    if (filters?.page) params.append("page", filters.page.toString());
    if (filters?.limit) params.append("limit", filters.limit.toString());

    const response = await api.get(`/hr/leaves?${params}`);
    // Normalize response shapes: older server returned { rows, total, limit, offset }
    // newer route returns { applications: { rows, total, limit, offset }, pagination: { page, limit, total } }
    let rows: any[] = [];
    let total = 0;
    let limitVal = filters?.limit || 10;
    let offset = 0;

    if (response.data) {
      if (response.data.rows) {
        // direct shape
        rows = response.data.rows;
        total = response.data.total || 0;
        limitVal = response.data.limit || limitVal;
        offset = response.data.offset || 0;
      } else if (response.data.applications) {
        // API returns applications + pagination
        const apps = response.data.applications;
        if (apps.rows) {
          rows = apps.rows;
          total =
            apps.total ||
            (response.data.pagination && response.data.pagination.total) ||
            0;
          limitVal =
            apps.limit ||
            (response.data.pagination && response.data.pagination.limit) ||
            limitVal;
          offset =
            apps.offset ||
            (response.data.pagination &&
              (response.data.pagination.page - 1) *
                (response.data.pagination.limit || limitVal)) ||
            0;
        } else if (Array.isArray(apps)) {
          // sometimes applications may be the array directly
          rows = apps;
          total = response.data.pagination?.total || apps.length;
          limitVal = response.data.pagination?.limit || limitVal;
          offset = ((response.data.pagination?.page || 1) - 1) * limitVal;
        }
      }
    }

    return {
      leaves: rows,
      total,
      currentPage: Math.floor(offset / limitVal) + 1,
      totalPages: limitVal ? Math.ceil(total / limitVal) : 1,
      limit: limitVal,
    };
  },
  // Manager Routes - Get team calendar
  getTeamCalendar: async (filters?: {
    departmentId?: number;
    startDate?: string;
    endDate?: string;
  }) => {
    const params = new URLSearchParams();
    if (filters?.departmentId)
      params.append("departmentId", filters.departmentId.toString());
    if (filters?.startDate) params.append("startDate", filters.startDate);
    if (filters?.endDate) params.append("endDate", filters.endDate);

    const response = await api.get(`/manager/team-calendar?${params}`);
    return response.data;
  },
  // Employment History
  getEmploymentHistory: async (employeeId?: number) => {
    const url = employeeId
      ? `/employment-history/employee/${employeeId}`
      : "/employment-history/mine";
    const response = await api.get(url);
    return response.data;
  },
};

export const departmentService = {
  getDepartments: async () => {
    const response = await api.get("/departments");
    return response.data;
  },
};
